import { useTranslation } from "react-i18next";
import { Plus } from "lucide-react";
import useReveal from "src/hooks/use-reveal";
import { CONTACTS } from "src/config/contacts";
import { genericWhatsappHref } from "src/utils/whatsapp";
import SectionBackdrop from "src/sections/components/section-backdrop";
import { SECTION_IMAGES } from "src/config/images";

// ЧАСТЫЕ ВОПРОСЫ.
//
// Порядок не случайный: сверху то, что человек спрашивает перед покупкой
// (как забронировать, сколько стоит виза, когда платить остаток), ниже —
// вопросы про формат поездки. Содержимое ответов взято из материалов
// компании, ничего не выдумано.
//
// Вёрстка на <details>/<summary>: раскрытие работает без JavaScript,
// клавиатурой и в скринридере — руками такое поведение не воспроизвести.
//
// Рамки у блока нет: фотография идёт во всю ширину окна, вопросы лежат прямо
// на ней. Скруглённая карточка делала из кадра картинку-плашку внутри
// страницы, а нужен вид как на первом экране.
const QUESTIONS = ["booking", "visa", "payment", "included", "language", "custom"];

export default function Faq() {
  const { t } = useTranslation();
  const reveal = useReveal();

  return (
    <section
      id="faq"
      ref={reveal.ref}
      className={`relative w-full py-10 sm:py-16 ${reveal.className}`}
    >
      {/* Фото во всю ширину окна — выбирается в src/config/images.js */}
      <SectionBackdrop image={SECTION_IMAGES.faq} />
      <div className="relative max-w-[1526px] mx-auto px-4">
        <div className="grid gap-8 lg:gap-14 lg:grid-cols-[minmax(0,22rem)_minmax(0,1fr)]">
          <div>
            <p className="on-photo text-xs sm:text-sm font-extrabold uppercase tracking-[0.28em] text-kj-gold">
              {t("faq.eyebrow")}
            </p>
            <h2 className="on-photo mt-3 text-2xl sm:text-4xl font-extrabold uppercase tracking-tight text-kj-ink text-balance">
              {t("faq.title")}
            </h2>
            <p className="on-photo mt-4 text-base text-kj-sub leading-relaxed max-w-[46ch]">
              {t("faq.subtitle")}
            </p>
            {CONTACTS.whatsapp && (
              <a
                href={genericWhatsappHref(t)}
                target="_blank"
                rel="noreferrer"
                className="mt-6 inline-flex items-center justify-center rounded-sm border border-kj-gold bg-kj-dark/40 px-6 py-3 text-xs font-bold uppercase tracking-[0.1em] text-kj-gold transition-colors hover:bg-kj-gold hover:text-kj-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-kj-gold focus-visible:ring-offset-2 focus-visible:ring-offset-kj-dark"
              >
                {t("faq.askButton")}
              </a>
            )}
          </div>

          <dl className="divide-y divide-kj-line border-t border-kj-line">
            {QUESTIONS.map((id) => (
              <details key={id} className="group py-4 sm:py-5">
                <summary className="flex cursor-pointer list-none items-start justify-between gap-4 rounded-sm text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-kj-gold focus-visible:ring-offset-4 focus-visible:ring-offset-kj-dark [&::-webkit-details-marker]:hidden">
                  <dt className="on-photo text-base sm:text-lg font-bold text-kj-ink leading-snug transition-colors group-hover:text-kj-gold group-open:text-kj-gold">
                    {t(`faq.items.${id}.q`)}
                  </dt>
                  {/* плюс поворачивается в крестик — видно, что блок открыт */}
                  <Plus
                    aria-hidden="true"
                    className="mt-1 h-5 w-5 flex-none text-kj-gold transition-transform duration-300 group-open:rotate-45"
                  />
                </summary>
                <dd className="on-photo mt-3 pr-9 text-sm sm:text-base text-kj-sub leading-relaxed max-w-[68ch]">
                  {t(`faq.items.${id}.a`)}
                </dd>
              </details>
            ))}
          </dl>
        </div>
      </div>
    </section>
  );
}
